import React, { lazy, LazyExoticComponent, Suspense } from 'react';
import {
  Route,
  Routes,
  useNavigate,
  NavigateFunction,
  useLocation,
  useParams,
} from 'react-router-dom';
import cx from 'classnames';
import { AxiosResponse } from 'axios';
import { KeycloakInstanceWrapper } from 'Keycloak/Keycloak';
import { observer } from 'mobx-react';
import { useKeycloak } from '@react-keycloak/web';
import Keycloak, { KeycloakInstance } from 'keycloak-js';
import * as Sentry from '@sentry/react';

import ProgressBar from '../ProgressBar/ProgressBar';
import ProtectedRoute from '../ProtectedRoute/ProtectedRoute';
import Instructions from '../Instructions/Instructions';
import PersonalDetails from '../PersonalDetails/PersonalDetails';
import QuestionnaireMagnum from '../QuestionnaireMagnum/QuestionnaireMagnum';
import QuestionnaireTAF from '../QuestionnaireTAF/QuestionnaireTAF';
import HealthDeclarationDocuments from '../HealthDeclarationDocuments/HealthDeclarationDocuments';
import Complete from '../Complete/Complete';
import Rejected from '../Rejected/Rejected';
import SinghostRedirectHandler from '../SignhostRedirectHandler/SinghostRedirectHandler';
import WaitForSigning from '../WaitForSigning/WaitForSigning';
import SignedHealthDec from '../SignedHealthDec/SignedHealthDec';
import ErrorScreen from '../ErrorScreen/ErrorScreen';
import AppStorage from '../Storage/Storage';
import DraftService from '../Requests/DraftService/DraftService';
import Requests from '../Requests/Requests';
import {
  changeHTMLDocumentLanguage,
  changePageTitle,
  isBrand,
  randomIntFromInterval,
  shiftNLToTheTopOfNationalitiesList,
  sortNationalities,
  transformInsuredToPersonalDataFormFormat,
  translate,
  translateWithLink2,
} from '../utils/utils';
import NationalityService from '../Requests/NationalityService';
import HealthDeclaration from '../interfaces/HealthDeclaration';
import HealthDeclarationStatus from '../enums/HealthDeclarationStatus';
import Draft from '../interfaces/Draft';
import { Loader } from '../Loading/Loading';
import UnderwriterOwner from '../enums/UnderwriterOwner';
import Modal from '../Admin/Modal/Modal';
import Labels from '../enums/Labels';
import Brand from '../enums/Brand';
import DocumentsService from '../Requests/DocumentsService';
import HealthDocFile from '../interfaces/HealthDocFile';
import ProductType from '../enums/ProductType';
import OccupationService from '../Requests/OccupationService';
import NavigationRoute from 'NavigationRoute/NavigationRoute';
import HealthDeclarationRoute from 'HealthDeclarationRoute/HealthDeclarationRoute';
import { HeaderWrapper } from '../Header/HeaderWrapper';
import { ProductSpecificData } from '../interfaces/ProductConfig';
import QuestionnaireReflex from '../QuestionnaireReflex/QuestionnaireReflex';
import { ReadSpeakerProvider } from 'ReadSpeakerReader/ReadSpeakerProvider';
import QuestionnaireAura from '../QuestionnaireAURA/QuestionnaireAura';

interface AppRootState {
  isLoading: boolean;
}

interface AppRootProps {
  navigate: NavigateFunction;
  location: Partial<Location>;
  isProgressBarHidden: boolean;
  generalError: string | null;
  keycloak: KeycloakInstance;
  healthDeclarationId: string;
}

const IsProgressBarShownByStatus: { [key: string]: boolean } = {
  [HealthDeclarationStatus.INVITATION_SENT]: true,
  [HealthDeclarationStatus.IN_PROGRESS]: true,
};

class AppRoot extends React.PureComponent<AppRootProps, AppRootState> {
  private readonly storage: AppStorage;

  private readonly draftService: DraftService;

  private readonly nationalityService: NationalityService;

  private readonly occupationService: OccupationService;

  private readonly documentsService: DocumentsService;

  private readonly lazyLoadedQuestionnaireMagnum: LazyExoticComponent<
    typeof QuestionnaireMagnum
  >;

  private readonly lazyLoadedQuestionnaireTAF: LazyExoticComponent<
    typeof QuestionnaireTAF
  >;

  private readonly lazyLoadedQuestionnaireReflex: LazyExoticComponent<
    typeof QuestionnaireReflex
  >;

  private readonly lazyLoadedQuestionnaireAura: LazyExoticComponent<
    typeof QuestionnaireAura
  >;

  private readonly timeoutId: number | null;

  private readonly navigate: NavigateFunction;

  private readonly location: Partial<Location>;

  constructor(props: AppRootProps) {
    super(props);
    const { keycloak, healthDeclarationId } = this.props;
    if (!keycloak.authenticated) {
      void keycloak.login({
        loginHint: healthDeclarationId,
      });
    } else {
      void keycloak
        .loadUserProfile()
        .then((profile: Keycloak.KeycloakProfile) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
          const [applicationId] = profile?.attributes?.applicationId || [
            'Old application',
          ];
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
          const [healthDeclarationType] = profile?.attributes
            ?.healthDeclarationType || ['Old application'];

          Sentry.configureScope(function (scope) {
            scope.setTag('Application Id', applicationId as string);
            scope.setTag(
              'Health declaration type',
              healthDeclarationType as string,
            );
            scope.setTag('Health declaration Id', healthDeclarationId);
          });
        });
    }
    this.navigate = props.navigate;
    this.location = props.location;
    this.storage = AppStorage.getInstance();
    this.draftService = new DraftService();
    this.nationalityService = new NationalityService();
    this.occupationService = new OccupationService();
    this.documentsService = new DocumentsService();
    this.lazyLoadedQuestionnaireMagnum = lazy(
      () => import('../QuestionnaireMagnum/QuestionnaireMagnum'),
    );
    this.lazyLoadedQuestionnaireTAF = lazy(
      () => import('../QuestionnaireTAF/QuestionnaireTAF'),
    );
    this.lazyLoadedQuestionnaireReflex = lazy(
      () => import('../QuestionnaireReflex/QuestionnaireReflex'),
    );

    this.lazyLoadedQuestionnaireAura = lazy(
      () => import('../QuestionnaireAURA/QuestionnaireAura'),
    );
    this.state = {
      isLoading: true,
    };
    this.timeoutId = null;
  }

  componentDidMount(): void {
    this.getLocale().finally(() => {
      this.initValidApplication();
      this.getBrandSpecificInformation();
    });
  }

  componentWillUnmount(): void {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId);
    }
  }

  getLocale = (): Promise<void> =>
    Requests.getLocale().then((response) => {
      const { result } = response.data;
      this.storage.setLocale(result);
      changeHTMLDocumentLanguage(result);
      changePageTitle(result);
    });

  checkIfPathWasChanged = (action: Location): void => {
    if (
      action.pathname.includes('conclusion') &&
      window.location.pathname.includes('questionnaire')
    ) {
      this.navigate(window.location.pathname);
    } else if (action.pathname !== window.location.pathname) {
      this.navigate(action.pathname);
    }
  };

  initValidApplication = (): void => {
    this.draftService
      .getDraft()
      .then(this.handleDraftResponse)
      .catch((error) => {
        console.log('draftResponse', error); // eslint-disable-line no-console
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (error?.response?.status === 404) {
          this.getHealthDeclarationData();
        } else {
          /*
           * Handle all errors reasonably later
           * */
          console.log(error); // eslint-disable-line no-console
          this.setIsLoading(false);
          throw error;
        }
      });
  };

  getBrandSpecificInformation = (): void => {
    this.setIsLoading(true);
    Requests.getProductConfig()
      .then((response) => {
        const {
          progressBarLabels,
          instructions,
          documentsInstructions,
          ...rest
        } = response.data.result;
        this.storage.setProductSpecificProgressBarLabels(progressBarLabels);
        this.storage.setProductSpecificInstructionsPage(instructions);
        this.storage.setProductSpecificDocumentsInstructions(
          documentsInstructions,
        );
        this.storage.setProductSpecificInfo(rest as ProductSpecificData);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        this.setIsLoading(false);
      });
  };

  getHealthDeclarationData = (
    shouldRedirectToErrorPageIfError = false,
  ): void => {
    Requests.getHealthDeclaration()
      .then((response) => {
        this.handleHealthDeclarationResponse(response);
      })
      .catch(() => {
        if (!shouldRedirectToErrorPageIfError) {
          this.getHealthDeclarationData(true);
        } else {
          this.redirectToErrorPage();
        }
      });
  };

  redirectToErrorPage = (): void => {
    const id = window.location.pathname.split('/')[1];
    this.navigate(`/${id}/error`);
    this.setIsLoading(false);
  };

  showProgressBar = (): void => {
    this.storage.setIsProgressBarHidden(false);
  };

  handleDraftResponse = (response: AxiosResponse<Draft>): void => {
    const { data } = response.data.result;

    try {
      this.draftService.parseDraftDto(data);
      this.getNationalities();
      this.getOccupations();
      this.getDocuments();
      this.showProgressBar();
      this.setIsLoading(false);
    } catch (e) {
      this.redirectToErrorPage();
      throw e;
    }
  };

  getNationalities = (): void => {
    if (this.storage.getNationalities.length === 0) {
      this.nationalityService
        .fetchNationalities(this.storage.getLocale)
        .then((nationalities) => {
          const sortedNationalities = sortNationalities(
            nationalities,
            'displayName',
          );
          shiftNLToTheTopOfNationalitiesList(sortedNationalities);

          this.storage.setNationalities(sortedNationalities);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  getOccupations = (): void => {
    const productType = this.storage.getProductType();
    const policyTerms = this.storage.getPolicyTerms();
    const isHBOrHa = productType === ProductType.HB || productType === ProductType.HA;
    const isWefox = isBrand(Brand.wefox);
    const isTaf = isBrand(Brand.Taf);

    if (
      this.storage.getOccupations.length === 0 &&
      ((isHBOrHa && isTaf) || isWefox)
    ) {
      this.occupationService
        .fetchOccupations(this.storage.getLocale, productType, policyTerms)
        .then((occupations) => {
          this.storage.setOccupations(occupations);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  };

  getDocuments = (): void => {
    this.documentsService
      .getUploadedDocuments()
      .then((docs) => {
        const healthDocFiles = docs.map((doc) => ({
          description: doc.description,
          isLoaded: true,
          isValid: true,
          id: randomIntFromInterval(1, 1000000),
          file: {
            name: doc.fileName,
            size: doc.fileSizeInBytes,
          },
        }));
        this.storage.setHealthDeclarationFileList(
          healthDocFiles as Array<HealthDocFile>,
        );
        /*
         * For DHMR we always show File upload, because we don't have toggle buttons to show/hide it.
         * */
        const isHealthDeclarationDocuments = isBrand(Brand.DHMR)
          ? true
          : !!docs.length;
        this.storage.setIsHealthDeclarationDocuments(
          isHealthDeclarationDocuments,
        );
        this.storage.setIsAllDocumentsValid(true);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  handleHealthDeclarationResponse(
    response: AxiosResponse<HealthDeclaration>,
  ): void {
    const {
      insured,
      status,
      signUrl = '',
      remarks,
      generalPractitioner,
      underwriterOwner,
      productType,
      policyTerms,
      deductPremiumFromTaxes,
    } = response.data.result;
    const id = window.location.pathname.split('/')[1];

    const personalData = transformInsuredToPersonalDataFormFormat(
      insured,
      generalPractitioner,
      this.storage,
    );
    this.storage.setPersonalDataFormState(personalData);
    this.storage.setSignUrl(signUrl);
    this.storage.setFinalComment(remarks || '');
    this.storage.setCurrentHDStatus(status);
    this.storage.setUnderwriterOwner(underwriterOwner);
    this.storage.setProductType(productType);
    this.storage.setPolicyTerms(policyTerms);
    this.storage.setDeductPremiumFromTaxes(deductPremiumFromTaxes || false);
    this.getNationalities();
    this.getOccupations();
    this.getDocuments();

    const keycloakInstance = KeycloakInstanceWrapper.getInstance();

    const isRedirectedFromSinghost =
      window.location.search && window.location.search.includes('sh_id');
    if (id && keycloakInstance) {
      keycloakInstance
        .loadUserProfile()
        .then((profile: Keycloak.KeycloakProfile) => {
          if (profile.username !== id) {
            keycloakInstance.logout().catch((err) => {
              console.error(err);
              throw new Error('Error with logout');
            });
          } else {
            if (
              (status === HealthDeclarationStatus.DOCUMENT_SENT_TO_SIGN_HOST ||
                status === HealthDeclarationStatus.WAITING_FOR_SIGNING) &&
              !isRedirectedFromSinghost
            ) {
              this.navigate(`/${id}/wait-for-signing`);
            }
            if (status === HealthDeclarationStatus.SIGNED) {
              this.navigate(`/${id}/signed`);
            } else if (status === HealthDeclarationStatus.ERROR) {
              this.navigate(`/${id}/error`);
            } else if (status === HealthDeclarationStatus.REJECTED) {
              this.navigate(`/${id}/rejected`);
            }
          }
          this.setIsLoading(false);
        })
        .catch((err: Error) => {
          throw new Error(err.message);
        });
    }

    if (IsProgressBarShownByStatus[status]) {
      this.showProgressBar();
    }
  }

  setIsLoading(isLoading: boolean): void {
    this.setState({
      isLoading: isLoading,
    });
  }

  render(): React.ReactElement {
    const { isLoading } = this.state;

    const { isProgressBarHidden, generalError } = this.props;

    let QuestionnaireComponent:
      | React.LazyExoticComponent<typeof QuestionnaireMagnum>
      | React.LazyExoticComponent<typeof QuestionnaireTAF>
      | React.LazyExoticComponent<typeof QuestionnaireReflex>
      | React.LazyExoticComponent<typeof QuestionnaireAura>;

    if (this.storage.getUnderwriterOwner && !isLoading) {
      QuestionnaireComponent = this.lazyLoadedQuestionnaireTAF;
      if (this.storage.getUnderwriterOwner === UnderwriterOwner.IPT_MAGNUM) {
        QuestionnaireComponent = this.lazyLoadedQuestionnaireMagnum;
      }
      if (this.storage.getUnderwriterOwner === UnderwriterOwner.QL_REFLEX) {
        QuestionnaireComponent = this.lazyLoadedQuestionnaireReflex;
      }
      if (this.storage.getUnderwriterOwner === UnderwriterOwner.ZEL_AURA) {
        QuestionnaireComponent = this.lazyLoadedQuestionnaireAura;
      }
    }

    const lang = this.storage.getLocale;
    const productSpecificData = this.storage.getProductSpecificInfo;

    return (
      <div className="App">
        <HeaderWrapper />
        <ReadSpeakerProvider />
        <div
          className={cx('App-navigation', {
            'App-navigation-hidden': isProgressBarHidden,
          })}
        >
          <ProgressBar />
        </div>
        <div className="App-content">
          {isLoading ? (
            <Loader />
          ) : (
            <Suspense fallback={<Loader />}>
              <Routes>
                <Route
                  path="/"
                  element={
                    <NavigationRoute
                      navigateTo={`instructions${this.location.search as string}`}
                    />
                  }
                />
                <Route
                  path="/instructions"
                  element={
                    <ProtectedRoute>
                      <HealthDeclarationRoute
                        render={(healthDeclarationId) => (
                          <>
                            <Instructions
                              locationSearch={this.location.search}
                              healthDeclarationId={healthDeclarationId}
                            />
                          </>
                        )}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/personal-info"
                  element={
                    <NavigationRoute
                      navigateTo={`personal-details${this.location.search as string}`}
                    />
                  }
                />
                <Route
                  path="/personal-details"
                  element={
                    <ProtectedRoute>
                      <HealthDeclarationRoute
                        render={(healthDeclarationId) => (
                          <>
                            <PersonalDetails
                              locationSearch={this.location.search}
                              healthDeclarationId={healthDeclarationId}
                            />
                          </>
                        )}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/questionnaire"
                  element={
                    <ProtectedRoute>
                      <HealthDeclarationRoute
                        render={(healthDeclarationId) => {
                          if (!this.storage.getUnderwriterOwner) {
                            return <Loader />;
                          }
                          return (
                            <>
                              <QuestionnaireComponent
                                locationSearch={this.location.search}
                                healthDeclarationId={healthDeclarationId}
                              />
                            </>
                          );
                        }}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/documents"
                  element={
                    <ProtectedRoute>
                      <HealthDeclarationRoute
                        render={(healthDeclarationId) => (
                          <>
                            <HealthDeclarationDocuments
                              locationSearch={this.location.search}
                              healthDeclarationId={healthDeclarationId}
                            />
                          </>
                        )}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/conclusion"
                  element={
                    <ProtectedRoute>
                      <HealthDeclarationRoute
                        render={(healthDeclarationId) => (
                          <>
                            <Complete
                              locationSearch={this.location.search}
                              healthDeclarationId={healthDeclarationId}
                            />
                          </>
                        )}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/rejected"
                  element={
                    <ProtectedRoute>
                      <HealthDeclarationRoute
                        render={(healthDeclarationId) => (
                          <>
                            <Rejected
                              locationSearch={this.location.search}
                              initWholeAppFn={this.initValidApplication}
                              healthDeclarationId={healthDeclarationId}
                            />
                          </>
                        )}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/wait-for-signing"
                  element={
                    <ProtectedRoute>
                      <HealthDeclarationRoute
                        render={(healthDeclarationId) => (
                          <>
                            <WaitForSigning
                              locationSearch={this.location.search}
                              initWholeAppFn={this.initValidApplication}
                              healthDeclarationId={healthDeclarationId}
                            />
                          </>
                        )}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/signed"
                  element={
                    <ProtectedRoute>
                      <SignedHealthDec />
                    </ProtectedRoute>
                  }
                />
                <Route path="/error" element={<ErrorScreen />} />
                <Route
                  path="/signing/return"
                  element={
                    <ProtectedRoute>
                      <HealthDeclarationRoute
                        render={(healthDeclarationId) => (
                          <>
                            <SinghostRedirectHandler
                              locationSearch={this.location.search}
                              healthDeclarationId={healthDeclarationId}
                            />
                          </>
                        )}
                      />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          )}
        </div>
        <Modal isShown={!!generalError} isSmall={true}>
          {generalError === 'NETWORK_ERROR' && (
            <>
              <h5>
                {translate(Labels.ERROR_BLOCK_CATEGORY_LOADING_HEADER, lang)}
              </h5>
              <div className="confirmation">
                <p
                  {...translateWithLink2(
                    Labels.ERROR_BLOCK_CATEGORY_LOADING_P,
                    lang,
                    [
                      productSpecificData.companyName,
                      productSpecificData.infoEmail,
                      productSpecificData.infoEmail,
                      productSpecificData.telephoneNumberHref,
                      productSpecificData.telephoneNumber,
                    ],
                  )}
                />
              </div>
            </>
          )}
        </Modal>
      </div>
    );
  }
}

const AppRootHoc = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storage = AppStorage.getInstance();
  const isProgressBarHidden = storage.getIsProgressBarHidden;
  const generalError = storage.getGeneralError;
  const { keycloak } = useKeycloak();
  const { healthDeclarationId } = useParams();

  return (
    <AppRoot
      location={location}
      navigate={navigate}
      isProgressBarHidden={isProgressBarHidden}
      generalError={generalError}
      keycloak={keycloak}
      healthDeclarationId={healthDeclarationId || ''}
    />
  );
};

export default observer(AppRootHoc);
